import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Ban, Banknote, PauseCircle, PlayCircle, ShieldCheck } from "lucide-react";
import AlphaPreferencesCard from "./AlphaPreferencesCard";
import AlphaSettingWidget from "./AlphaSettingWidget";
import {
  getCashDeploymentSubtext,
  getCashDeploymentSummary,
  resolveCashDeploymentMode,
} from "../../utils/cashDeploymentSettings";
import { getTargetConfigValue, getTargetUserSettings } from "./alphaPageSubject";
import type { AlphaProfilePageProps } from "./alphaPageSubject";
import userContractExclusionsService from "../../services/userContractExclusionsService";
import subscriptionService from "../../services/subscriptionService";
import {
  emitAdminCustomerViewOwnerMismatch,
} from "../admin/adminCustomerViewDiagnostics";
import type { UserContractExclusion } from "../../services/userContractExclusionsService";
import type { SubscriptionWithRelations } from "../../types/subscription";
import {
  getInvestorAccessSummary,
  normalizeSubscriptionSettings,
} from "../../types/subscription";

type TargetResourceLoadStatus = "idle" | "loading" | "loaded" | "failed";

function getCombinedAccessSummary(subscription: SubscriptionWithRelations): string {
  const settings = normalizeSubscriptionSettings(subscription.subscriptionSettings);
  return [
    `Portfolios: ${getInvestorAccessSummary(settings.portfolios)}`,
    `Baskets: ${getInvestorAccessSummary(settings.baskets)}`,
  ].join("; ");
}

export default function AdminTargetProfileAdapter(
  props: Extract<AlphaProfilePageProps, { mode: "admin-target" }>
): React.ReactElement {
  const { subject, questionnaire, onRetryQuestionnaire } = props;
  const roles = subject.user.roles ?? [];
  const showSubscriptions = roles.includes("INVESTOR") || roles.length === 0;
  const [exclusions, setExclusions] = useState<UserContractExclusion[]>([]);
  const [exclusionsStatus, setExclusionsStatus] = useState<TargetResourceLoadStatus>("loading");
  const [exclusionsOwnerUserId, setExclusionsOwnerUserId] = useState(subject.userId);
  const [subscriptions, setSubscriptions] = useState<SubscriptionWithRelations[]>([]);
  const [subscriptionsStatus, setSubscriptionsStatus] = useState<TargetResourceLoadStatus>(
    showSubscriptions ? "loading" : "idle"
  );
  const [subscriptionsOwnerUserId, setSubscriptionsOwnerUserId] = useState(subject.userId);
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  const exclusionsReqRef = useRef(0);
  const subscriptionsReqRef = useRef(0);

  const exclusionsAreCurrent = exclusionsOwnerUserId === subject.userId;
  const visibleExclusionsStatus: TargetResourceLoadStatus = exclusionsAreCurrent
    ? exclusionsStatus
    : "loading";
  const visibleExclusions = visibleExclusionsStatus === "loaded" ? exclusions : [];
  const subscriptionsAreCurrent = subscriptionsOwnerUserId === subject.userId;
  const visibleSubscriptionsStatus: TargetResourceLoadStatus = showSubscriptions
    ? subscriptionsAreCurrent ? subscriptionsStatus : "loading"
    : "idle";
  const visibleSubscriptions = visibleSubscriptionsStatus === "loaded" ? subscriptions : [];
  const cashVisible = getTargetConfigValue(subject.userConfig, "feature_cash_deploy_mode") === "true";
  const adminTradingEnabled = getTargetConfigValue(subject.userConfig, "tradingenabled") === "true";
  const userSettings = getTargetUserSettings(subject.userConfig);
  const userTradingEnabled = userSettings?.user_tradingenabled !== false;
  const accountTrading = adminTradingEnabled && userTradingEnabled;
  const cashMode = resolveCashDeploymentMode(userSettings);
  const cashSummary = getCashDeploymentSummary(cashMode);
  const cashSubtext = getCashDeploymentSubtext(cashMode);

  const loadExclusions = useCallback(async () => {
    const reqId = ++exclusionsReqRef.current;
    setExclusions([]);
    setExclusionsOwnerUserId(subject.userId);
    setExclusionsStatus("loading");
    try {
      const result = await userContractExclusionsService.getExclusionsForUser(subject.userId);
      if (reqId !== exclusionsReqRef.current) return;
      const rows = result.exclusions;
      if (rows.some((row) => row.userId !== subject.userId)) {
        emitAdminCustomerViewOwnerMismatch("exclusions");
        setExclusions([]);
        setExclusionsStatus("failed");
        return;
      }
      setExclusions(rows);
      setExclusionsStatus("loaded");
    } catch {
      if (reqId !== exclusionsReqRef.current) return;
      setExclusions([]);
      setExclusionsStatus("failed");
    }
  }, [subject.userId]);

  const loadSubscriptions = useCallback(async () => {
    const reqId = ++subscriptionsReqRef.current;
    setSubscriptionOpen(false);
    setSubscriptions([]);
    setSubscriptionsOwnerUserId(subject.userId);
    if (!showSubscriptions) {
      setSubscriptionsStatus("idle");
      return;
    }
    setSubscriptionsStatus("loading");
    try {
      const rows = await subscriptionService.listAdminInvestorSubscriptionsAll(subject.userId);
      if (reqId !== subscriptionsReqRef.current) return;
      if (rows.some((row) => (
        row.subscriptionUserId !== subject.userId || row.subscriptionUser.id !== subject.userId
      ))) {
        emitAdminCustomerViewOwnerMismatch("subscriptions");
        setSubscriptions([]);
        setSubscriptionsStatus("failed");
        return;
      }
      setSubscriptions(rows);
      setSubscriptionsStatus("loaded");
    } catch {
      if (reqId !== subscriptionsReqRef.current) return;
      setSubscriptions([]);
      setSubscriptionsStatus("failed");
    }
  }, [showSubscriptions, subject.userId]);

  useEffect(() => {
    void loadExclusions();
    void loadSubscriptions();
  }, [loadExclusions, loadSubscriptions]);

  const rebalancingLabel = useMemo(() => {
    if (accountTrading) return "Trading active";
    if (userTradingEnabled) return "System pending";
    return "Rebalancing paused";
  }, [accountTrading, userTradingEnabled]);

  return (
    <div className="mx-auto w-full max-w-[1080px] px-4 pb-16 pt-7 md:px-10">
      <h1 className="font-v7-serif text-3xl font-normal text-v7-ink">Profile</h1>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {questionnaire.status === "loading" ? (
          <div className="h-64 animate-pulse rounded-v7-panel bg-v7-mist" role="status" />
        ) : (
          <AlphaPreferencesCard
            variant="tile"
            progressResponses={questionnaire.status === "loaded" ? questionnaire.progressResponses : null}
            displayOnly
            requestFailed={questionnaire.status === "failed"}
            onRetry={onRetryQuestionnaire}
          />
        )}
        <AlphaSettingWidget
          icon={<Ban className="h-5 w-5" />}
          title="Contract exclusions"
          description="Symbols this customer excludes from trading"
          summary={visibleExclusionsStatus === "loading"
            ? "Loading exclusions"
            : `${visibleExclusions.length} exclusions`}
          error={visibleExclusionsStatus === "failed" ? "Exclusions unavailable." : null}
        />
        {showSubscriptions && (
          <AlphaSettingWidget
            icon={<ShieldCheck className="h-5 w-5" />}
            title="Subscriptions"
            description="Provider subscriptions"
            summary={visibleSubscriptionsStatus === "loading"
              ? "Loading subscriptions"
              : `${visibleSubscriptions.length} subscriptions`}
            error={visibleSubscriptionsStatus === "failed" ? "Subscriptions unavailable." : null}
            actionLabel={visibleSubscriptionsStatus === "loaded" ? "View" : undefined}
            onAction={visibleSubscriptionsStatus === "loaded"
              ? () => setSubscriptionOpen(true)
              : undefined}
          />
        )}
        {cashVisible && (
          <AlphaSettingWidget
            icon={<Banknote className="h-5 w-5" />}
            title="Cash deployment"
            description="How idle cash is used"
            summary={cashSummary}
            subtext={cashSubtext}
          />
        )}
        <AlphaSettingWidget
          icon={accountTrading ? <PlayCircle className="h-5 w-5" /> : <PauseCircle className="h-5 w-5" />}
          title="Automated rebalancing"
          description="Trading and rebalance status"
          summary={rebalancingLabel}
        />
      </div>
      {subscriptionOpen && visibleSubscriptionsStatus === "loaded" && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[80vh] w-full max-w-lg overflow-auto rounded-xl border border-gray-200 bg-white p-6 dark:border-rp-separator dark:bg-rp-surface-raised">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Subscriptions</h2>
            {visibleSubscriptions.length === 0 ? (
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">No subscriptions.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {visibleSubscriptions.map((row) => (
                  <li key={row.id} className="text-sm text-gray-700 dark:text-gray-300">
                    {row.provider.name} — {getCombinedAccessSummary(row)}
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={() => setSubscriptionOpen(false)}
              className="mt-6 rounded-lg bg-rp-amber px-3 py-2 text-sm font-semibold text-white"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
